// Intent detection for YNX — smart routing to the right tool
// when the user asks for image/video/search in chat.

// Verbs that signal image generation
const IMAGE_VERBS = [
  'cr[ée]e?', 'cr[ée]er', 'g[ée]n[ée]re?', 'g[ée]n[ée]rer', 'fais', 'fabrique',
  'dessine', 'dessiner', 'imagine', 'imaginer', 'transforme', 'modifie', 'mets', 'place', 'mets-moi',
  'fais-moi', 'donne', 'donne-moi', 'donner', 'fournis', 'fournir', 'envoie', 'envoyer',
  'montre', 'montrer', 'propose', 'proposer', 'produis', 'produire',
  'illustre', 'illustrer', 'peins', 'peindre', 'r[ée]alise', 'r[ée]aliser',
  'sors', 'sortir', 'cr[ée]e?-moi', 'g[ée]n[ée]re?-moi',
];
const VIDEO_VERBS = ['anime', 'animer', 'fais bouger', 'mets en mouvement', 'fais une vid[ée]o', 'cr[ée]e?-moi une vid[ée]o'];

// Noun signals
const IMAGE_NOUNS = [
  'photo', 'photos', 'image', 'images', 'portrait', 'portraits', 'visuel', 'visuels',
  'illustration', 'illustrations', 'rendu', 'rendus', 'clich[ée]', 'tableau', 'tableaux',
  'art', 'avatar', 'avatars', 'personnage', 'personnages', 'character', 'characters',
  'h[ée]ros', 'mascotte', 'mascottes', 'design', 'designs', 'concept', 'concepts',
  'concept[- ]art', 'sc[èe]ne', 'sc[èe]nes', 'logo', 'logos', 'affiche', 'affiches',
  'po[sz]ter', 'po[sz]ters', 'cover', 'covers', 'pochette', 'pochettes',
  'sticker', 'stickers', 'emoji', 'emojis',
];
const VIDEO_NOUNS = ['vid[ée]o', 'animation', 'clip', 'film'];

// Search trigger words — expanded for "live, world-aware" YNX
const SEARCH_REGEX = /\b(cherche|recherche|trouve(-moi)?|trouver|google|web|sur internet|sur le net|en ligne|actualit[ée]s?|news|derni[èe]res info|infos? r[ée]cente|qu['"]?est[- ]ce qu['"]?il se passe|que se passe[- ]t[- ]il|en direct|temps r[ée]el|en ce moment|aujourd['"]?hui|hier|cette semaine|ce mois|ce matin|prix de|cours de|combien co[ûu]te|m[ée]t[ée]o|qui a gagn[ée]|score|r[ée]sultat|annonce|sortie|release|nouveau mod[èe]le|new model|lanc[ée] aujourd|2026|2025)\b/i;

const buildRegex = (verbs, nouns) => {
  const v = verbs.join('|');
  const n = nouns.join('|');
  return new RegExp(`(${v})\\b[\\s\\S]{0,80}(${n})\\b|\\b(${n})\\b[\\s\\S]{0,60}(${v})`, 'i');
};

const IMAGE_REGEX = buildRegex(IMAGE_VERBS, IMAGE_NOUNS);
const VIDEO_REGEX = buildRegex(VIDEO_VERBS.concat(IMAGE_VERBS), VIDEO_NOUNS);

// Bare-noun trigger (no verb required): when the user clearly asks for a count of visuals
// e.g. "les 7 personnages", "3 logos", "5 illustrations"
const IMAGE_COUNT_REGEX = new RegExp(`\\b(\\d{1,2}|un|une|deux|trois|quatre|cinq|six|sept|huit|neuf|dix)\\s+(${IMAGE_NOUNS.join('|')})\\b`, 'i');

/**
 * Detect intent in a user message.
 * Returns one of: 'video', 'image', 'search', 'chat'
 */
export const detectIntent = (text, hasImageAttachment = false) => {
  if (!text) return 'chat';
  const t = text.toLowerCase();
  // Video has priority because "crée une vidéo" matches image regex too
  if (VIDEO_REGEX.test(t)) return 'video';
  if (IMAGE_REGEX.test(t)) return 'image';
  // Bare-count fallback: "les 7 personnages", "3 logos"
  if (IMAGE_COUNT_REGEX.test(t)) return 'image';
  // Web search detection
  if (SEARCH_REGEX.test(t)) return 'search';
  // If image is attached and the message looks like an action request, lean image
  if (hasImageAttachment) {
    if (/\b(mets|met|place|transforme|fais|stylise|colorise|am[ée]liore|retouche|fond)\b/i.test(t)) return 'image';
  }
  return 'chat';
};
